/**
 * PWA Utilities
 * Helper functions for Progressive Web App functionality
 */

// Check if running in standalone mode
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ||
    document.referrer.includes('android-app://')
  );
}

// Check if device is iOS
export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream;
}

// Check if device is Android
export function isAndroid(): boolean {
  return /Android/.test(navigator.userAgent);
}

// Check if device is MacOS
export function isMac(): boolean {
  return navigator.userAgent.includes('Macintosh');
}

// Check if browser is Safari (and not Chrome/Edge which also include "Safari" in UA)
export function isSafari(): boolean {
  return navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
}

// Get device type for analytics
export function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const userAgent = navigator.userAgent;
  
  if (/tablet|iPad/i.test(userAgent)) {
    return 'tablet';
  }
  
  if (/mobile|iPhone|iPod|Android.*Mobile/i.test(userAgent)) {
    return 'mobile';
  }
  
  return 'desktop';
}

// Register service worker
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  // Disable PWA service worker in Vercel Preview environments to prevent caching issues
  // Preview URLs typically look like: project-name-git-branch-user.vercel.app
  if (window.location.hostname.includes('-git-') && window.location.hostname.includes('.vercel.app')) {
    console.log('🚫 PWA Service Worker disabled in Vercel Preview environment');
    return null;
  }

  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported');
    return null;
  }

  try {
    console.log('📋 Registering service worker...');
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none' // Always check for updates
    });

    console.log('✅ Service Worker registered:', registration.scope);

    // Check for updates immediately and then every 60 seconds
    registration.update()?.catch?.(err => console.debug('Service Worker update skipped:', err));
    setInterval(() => {
      registration.update()?.catch?.(err => console.debug('Service Worker interval update skipped:', err));
    }, 60000);

    return registration;
  } catch (error) {
    console.error('❌ Service Worker registration failed:', error);
    return null;
  }
}

// Check if app can be installed
export function canInstallPWA(): boolean {
  return (
    !isStandalone() && 
    ('serviceWorker' in navigator) &&
    (window as Window & { BeforeInstallPromptEvent?: unknown }).BeforeInstallPromptEvent !== undefined
  );
}

// Show iOS install instructions
export function showIOSInstallInstructions(): void {
  const message = `
Para instalar o Lumo no seu iPhone/iPad:

1. Toque no ícone de compartilhar (□↗) na parte inferior da tela
2. Role para baixo e selecione "Adicionar à Tela de Início"
3. Toque em "Adicionar" no canto superior direito

O app aparecerá na sua tela inicial como um app nativo!
  `.trim();

  alert(message);
}

// Show Mac Safari install instructions
export function showMacSafariInstallInstructions(): void {
  const message = `
Para instalar o Lumo no Mac (Safari):

1. Na barra de menu, clique em "Arquivo" ou no ícone de compartilhar
2. Selecione "Adicionar ao Dock..."
3. Clique em "Adicionar"

O app aparecerá no seu Dock e Launchpad!
  `.trim();

  alert(message);
}

// Show Chrome/Edge install instructions
export function showChromeInstallInstructions(): void {
  const message = `
Para instalar o Lumo no Chrome/Edge:

1. Procure o ícone de instalação (🖥️ ou ➕) na barra de endereço (canto superior direito)
2. Clique em "Instalar"

OU

1. Clique no menu do navegador (⋮ ou •••)
2. Selecione "Salvar e compartilhar" > "Instalar Lumo..."
  `.trim();

  alert(message);
}

// Get install prompt instructions for different platforms
export function getInstallInstructions(platform: string = ''): string {
  if (isIOS()) {
    return 'Toque em compartilhar (□↗) e selecione "Adicionar à Tela de Início"';
  }

  if (isMac() && isSafari()) {
    return 'Clique em Arquivo > Adicionar ao Dock';
  }

  if (isAndroid()) {
    return 'Toque no menu (⋮) e selecione "Adicionar à tela inicial" ou "Instalar app"';
  }

  return 'Use o menu do navegador para instalar o app';
}

// Analytics helper for PWA events
export function trackPWAEvent(eventName: string, properties?: Record<string, unknown>): void {
  console.log('📊 PWA Event:', eventName, properties);
  
  // You can integrate with your analytics service here
  // Example: gtag('event', eventName, properties);
}

// Check if feature is supported
export function supportsNotifications(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator;
}

export function supportsBackgroundSync(): boolean {
  return 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype;
}

export function supportsPush(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// PWA installation analytics
export function onPWAInstalled(): void {
  trackPWAEvent('pwa_installed', {
    device_type: getDeviceType(),
    platform: isIOS() ? 'ios' : isAndroid() ? 'android' : 'desktop',
    timestamp: new Date().toISOString()
  });
}

export function onPWAInstallPromptShown(): void {
  trackPWAEvent('pwa_install_prompt_shown', {
    device_type: getDeviceType(),
    timestamp: new Date().toISOString()
  });
}

export function onPWAInstallPromptAccepted(): void {
  trackPWAEvent('pwa_install_prompt_accepted', {
    device_type: getDeviceType(),
    timestamp: new Date().toISOString()
  });
}

export function onPWAInstallPromptDismissed(): void {
  trackPWAEvent('pwa_install_prompt_dismissed', {
    device_type: getDeviceType(),
    timestamp: new Date().toISOString()
  });
}